/**
 * useFeedbackGate.ts
 * Returns true when the feedback widget should be visible.
 *
 * Gate logic (SPEC-feedback-001 §4.2):
 *   show if (VITE_FEEDBACK_ENABLED === "true")
 *   OR user has a platform internal role (ADMIN/MANAGER)
 *   OR user has an external/internal test role (tester/internal/qa)
 *
 * The env flag still requires authentication, so anonymous browsers on staging
 * URLs do not see the widget.
 */

import { UserRole } from '@archer/shared'
import { useAuth } from '../../hooks/useAuth'

/** Role values that unlock the feedback widget regardless of env flag. */
const PLATFORM_INTERNAL_ROLES = [UserRole.ADMIN, UserRole.MANAGER].map((role) => role.toLowerCase())
const EXTERNAL_INTERNAL_ROLES = ['tester', 'internal', 'qa'] as const

/**
 * Pure gate decision. Shared by the hook AND by route-level guards (e.g. the
 * /feedback/mine page) so they agree off the SAME auth snapshot.
 *
 * Why pure: `useAuth` is a per-hook (not a shared context), so each call mounts
 * its own state + fetch. Calling the hook in two places (page + gate) races —
 * one instance can resolve while the other is still loading (user null) and wrongly
 * deny. Feeding both decisions from a single useAuth() avoids that race.
 */
export function isFeedbackAllowed(
  user: { role?: string | null } | null | undefined,
  isAuthenticated: boolean,
  envFlagEnabled: boolean = import.meta.env['VITE_FEEDBACK_ENABLED'] === 'true'
): boolean {
  // Env flag alone is enough for demo/staging builds — belt-and-suspenders: also
  // require authentication so an unauthenticated browser on a staging URL stays clean.
  if (envFlagEnabled && isAuthenticated) return true
  if (!user) return false
  const role = user.role?.toLowerCase() ?? ''
  return (
    PLATFORM_INTERNAL_ROLES.some((internalRole) => role === internalRole) ||
    EXTERNAL_INTERNAL_ROLES.some((externalRole) => role.includes(externalRole))
  )
}

export function isFeedbackOpsAllowed(
  user: { role?: string | null } | null | undefined,
  isAuthenticated: boolean
): boolean {
  if (!isAuthenticated || !user) return false
  const role = user.role?.toLowerCase() ?? ''
  return (
    PLATFORM_INTERNAL_ROLES.some((internalRole) => role === internalRole) ||
    role.includes('internal') ||
    role.includes('qa')
  )
}

export function useFeedbackGate(): boolean {
  const { user, isAuthenticated } = useAuth()
  return isFeedbackAllowed(user, isAuthenticated)
}

export function useFeedbackOpsGate(): boolean {
  const { user, isAuthenticated } = useAuth()
  return isFeedbackOpsAllowed(user, isAuthenticated)
}
